import React, { useState } from 'react';
import { Button, Form, Input, message, Select } from 'antd';
import mentorEditFormStyles from './MentorEditForm.module.css';
import { useUserStore } from '../../../../context/UserStore';
import { formatDate, formatTime } from '../../../../utils/dateFormat';

const { TextArea } = Input;

export const YES_NO_NA_OPTIONS = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'n/a', label: 'Not Applicable' },
];

export const REQUIRED_COMMENT = [{ required: true, message: 'Please enter comments!' }];
export const REQUIRED_CHOICE = [{ required: true, message: 'Please select feedback type!' }];

// Shared chrome for every "View Mentor" screen. Each conversation type supplies its own
// `fields` descriptor; only those fields are ever sent, so editing a Development Dialogue
// can never write the Coaching columns onto it.
export default function MentorEditForm({ title, employee, mentor, fields, onSaved }) {
    const [saving, setSaving] = useState(false);
    const user = useUserStore((state) => state.user);
    const { employee_id: employeeId } = mentor.attributes;

    // antd treats null as a controlled value and warns; undefined leaves the input empty.
    const initialValues = fields.reduce((values, field) => {
        const stored = mentor.attributes[field.name];
        return { ...values, [field.name]: stored === null ? undefined : stored };
    }, {});

    const onFinish = async (values) => {
        const payload = fields.reduce((attributes, field) => (
            { ...attributes, [field.name]: values[field.name] }
        ), {});

        setSaving(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/v1/users/employees/${employeeId}/mentors/${mentor.id}`, {
                method: 'put',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}`,
                },
                body: JSON.stringify({ mentor: payload }),
                type: 'json'
            });
            if (!response.ok) {
                throw Error(response.statusText);
            }
            const data = await response.json();
            if (data.success === false) {
                message.error(data.errors || 'This conversation could not be saved.');
                return;
            }
            message.success('Feedback successfully updated');
            if (onSaved) {
                onSaved();
            }
        } catch (error) {
            console.log(error);
            message.error('This conversation could not be saved.');
        } finally {
            setSaving(false);
        }
    };

    const renderControl = (field) => {
        if (field.input === 'select') {
            return <Select options={field.options} />;
        }
        return <TextArea rows={4} placeholder={field.placeholder || 'Elaborate'} />;
    };

    return (
        <>
            <div className={`py-5 grid grid-cols-1 mb-5 ${mentorEditFormStyles.headerBar}`}>
                <h1 className='text-4xl text-center text-white'>{title}</h1>
            </div>
            <div className='flex flex-col justify-left'>
                <h1 className='text-xl'>
                    <strong>Name:</strong> {employee.attributes.first_name} {employee.attributes.last_name}
                </h1>
                <p className='text-lg'><strong>Email:</strong> {employee.attributes.email}</p>
                <h1 className='text-xl'><strong>Date:</strong> {formatDate(mentor.attributes.occurred_at)}</h1>
                <p className='text-lg'><strong>Time:</strong> {formatTime(mentor.attributes.occurred_at)}</p>
            </div>
            <div className='grid grid-cols-6 gap-4'>
                <div className='col-span-6'>
                    <Form
                        onFinish={onFinish}
                        onFinishFailed={(errorInfo) => console.log('Failed:', errorInfo)}
                        labelWrap
                        labelAlign='left'
                        layout='vertical'
                        className='w-full'
                        initialValues={initialValues}
                    >
                        <div className='shadow sm:rounded-md sm:overflow-hidden'>
                            {fields.map((field) => (
                                <div key={field.name} className='px-4 py-5 bg-white space-y-6 sm:p-6'>
                                    <div className='grid grid-cols-6 gap-6'>
                                        <div className='col-span-12 sm:col-span-2'>
                                            <Form.Item
                                                label={field.label}
                                                name={field.name}
                                                className='col-span-6 sm:col-span-2'
                                                rules={field.rules}
                                            >
                                                {renderControl(field)}
                                            </Form.Item>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <div className='px-4 py-5 bg-white space-y-6 sm:p-6'>
                                <div className='grid grid-cols-6 gap-6'>
                                    <div className='col-span-12 sm:col-span-2'>
                                        <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
                                            <Button
                                                className='bg-blue-500 hover:bg-blue-700 text-white font-bold px-4 rounded'
                                                htmlType='submit'
                                                loading={saving}
                                            >
                                                Submit
                                            </Button>
                                        </Form.Item>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Form>
                </div>
            </div>
        </>
    )
}
